import React, { useEffect, useState } from "react";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmtNum } from "@/lib/money";
import { useChrome, useOpeners } from "../ui/MobileShell";
import PayoutDomainSection from "@/components/payouts/PayoutDomainSection";
import { usePayoutDomainEnabled } from "@/components/payouts/usePayouts";

/* MobileWallet (design 5a) — PCash leads (it's the one with real value + rules),
   then PCoins, an AMOE row, and a REAL activity list from GET /wallet. Pushed
   screen chrome (back + title). The + cashier is the designed Redeem (5b) /
   Coin Store (5c) sheets, opened via useOpeners(). REAL data only:
     • balances       → AuthContext.user (coins/cash/bonus)
     • verified pill  → GET /kyc/verification (can_redeem) / user.kyc_status
     • activity       → GET /wallet .transactions (per-currency marks, never blended)
   Undecided commercial facts render honest TBD ("processing time TBD"). */

const GREEN = "#00D632";
const SILVER = "#E4E4E7";

// Transaction type → human label + whether it's a credit (green +) or debit (−).
const TYPE_LABEL = {
  deposit: "Purchase", withdraw: "Prize redemption", bet: "Bet placed",
  payout: "Winning payout", admin_credit: "Admin credit", bonus: "Bonus PCoins",
  tip_sent: "Tip sent", tip_received: "Tip received",
};
const CREDIT_TYPES = ["deposit", "payout", "admin_credit", "bonus", "tip_received"];
// Icon chip glyph per type.
const TYPE_ICON = {
  deposit: "pnrcoin", withdraw: "upload", bet: "sportsbook", payout: "pnrcoin",
  admin_credit: "pnrcoin", bonus: "sparkles", tip_sent: "transfer", tip_received: "transfer",
};

// Map a transaction row to the money model's currency word (mirrors desktop Wallet.jsx).
function txCurrency(t) {
  const c = t?.currency;
  if (c === "cash" || c === "bonus" || c === "coins") return c;
  if (["withdraw", "payout", "admin_credit"].includes(t?.type)) return "cash";
  return "coins";
}

export default function MobileWallet() {
  useChrome({ mode: "back", title: "Wallet" });
  const { user, streamerMode } = useAuth();
  const { openCashier } = useOpeners();
  const { enabled: payoutDomainEnabled } = usePayoutDomainEnabled();
  // When the new payout domain owns redemptions, route the legacy Redeem button to the new
  // flow (the Cash Redemptions section at the top) instead of the fenced legacy cashier.
  const onRedeem = () => {
    if (payoutDomainEnabled) {
      document.getElementById("payout-redemptions")?.scrollIntoView({ behavior: "smooth" });
    } else {
      openCashier("redeem");
    }
  };
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [verif, setVerif] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/wallet");
      setData(data); setLoadErr(false);
    } catch { setLoadErr(true); }
  };
  useEffect(() => {
    load();
    api.get("/kyc/verification").then(({ data }) => setVerif(data)).catch(() => {});
  }, []);

  const mask = !!streamerMode;
  const coins = user?.coins ?? 0;
  const cash = user?.cash ?? 0;
  const bonus = user?.bonus ?? 0;

  // Verified = eligibility actually permits redeeming (never just the ID flag).
  const idApproved = (verif?.identity_status || user?.kyc_status) === "approved";
  const verified = verif ? !!verif.can_redeem : idApproved;

  const txns = Array.isArray(data?.transactions) ? data.transactions : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "14px 16px 0" }}>
      {/* New payout domain — renders only when enabled (synthetic env); otherwise null. */}
      <PayoutDomainSection compact />
      {/* PCash card — leads */}
      <div style={{ position: "relative", borderRadius: 22, padding: 20, overflow: "hidden",
        background: "radial-gradient(120% 120% at 100% 0%,rgba(255,255,255,.07) 0%,rgba(255,255,255,0) 55%),linear-gradient(180deg,#1A1A1E 0%,#111114 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08),inset 0 1px 0 rgba(255,255,255,.12),0 16px 36px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="pnrcash" size={28} style={{ color: SILVER }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "800 16px 'Manrope'", color: "#fff" }}>PCash</div>
            <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>Redeemable 1:1 for cash</div>
          </div>
          {verified && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 24, padding: "0 9px", borderRadius: 999, background: "rgba(0,214,50,.12)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.4)", font: "700 11px 'Outfit'", color: GREEN }}>
              <Icon name="check-circle" size={12} style={{ color: GREEN }} /> Verified
            </span>
          )}
        </div>
        <div className="m-tnum" style={{ marginTop: 14, font: "800 40px 'Manrope'", letterSpacing: "-.04em", color: "#fff" }}>
          {mask ? "•••••" : `$${Number(cash).toFixed(2)}`}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
          <span style={{ font: "500 12px 'Outfit'", color: "#71717a" }}>$50 minimum · processing time TBD</span>
          <button type="button" onClick={onRedeem} data-testid="wallet-redeem"
            style={{ height: 44, flex: "none", display: "flex", alignItems: "center", gap: 7, padding: "0 16px", borderRadius: 14, background: "#F4F4F5", color: "#0A0A0A", font: "800 14px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)" }}>
            <Icon name="upload" size={16} style={{ color: "#0A0A0A" }} /> Redeem
          </button>
        </div>
      </div>

      {/* PCoins card */}
      <div style={{ position: "relative", borderRadius: 22, padding: 20, overflow: "hidden",
        background: "radial-gradient(120% 120% at 100% 0%,rgba(0,214,50,.09) 0%,rgba(0,214,50,0) 55%),linear-gradient(180deg,#1A1A1E 0%,#111114 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08),inset 0 1px 0 rgba(255,255,255,.12),0 16px 36px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="pnrcoin" size={28} style={{ color: GREEN }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "800 16px 'Manrope'", color: "#fff" }}>PCoins</div>
            <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>Free play · no cash value</div>
          </div>
        </div>
        <div className="m-tnum" style={{ marginTop: 14, font: "800 40px 'Manrope'", letterSpacing: "-.04em", color: "#fff" }}>
          {mask ? "•••••" : fmtAmtNum(coins, "coins")}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
          <span style={{ font: "500 12px 'Outfit'", color: "#71717a" }}>Packs include free PCash</span>
          <button type="button" onClick={() => openCashier("get")} data-testid="wallet-get-coins"
            style={{ height: 44, flex: "none", display: "flex", alignItems: "center", gap: 7, padding: "0 16px", borderRadius: 14, background: GREEN, color: "#0A0A0A", font: "800 14px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)" }}>
            <Icon name="plus" size={16} style={{ color: "#0A0A0A" }} /> Get Coins
          </button>
        </div>
      </div>

      {/* Bonus funds — only when the account actually holds locked bonus */}
      {bonus > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, borderRadius: 18, padding: "14px 16px", background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          <Icon name="pnrcash" size={22} style={{ color: SILVER }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "700 14px 'Outfit'", color: "#fff" }}>Bonus funds</div>
            <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>Wager to unlock as PCash</div>
          </div>
          <span className="m-tnum" style={{ font: "800 16px 'Manrope'", color: GREEN }}>{mask ? "•••" : `$${Number(bonus).toFixed(2)}`}</span>
        </div>
      )}

      {/* AMOE row */}
      <a href="/sweeps-rules" style={{ display: "flex", alignItems: "center", gap: 12, height: 52, padding: "0 16px", borderRadius: 14, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)", textDecoration: "none" }}>
        <Icon name="mail" size={20} style={{ color: "#a1a1aa" }} />
        <span style={{ flex: 1, font: "600 15px 'Outfit'", color: "#fff" }}>Free PCash by mail (AMOE)</span>
        <Icon name="chevron-right" size={16} style={{ color: "#52525b" }} />
      </a>

      {/* Activity */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ font: "800 17px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Activity</span>
      </div>
      {loadErr ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "22px 16px", borderRadius: 18, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          <span style={{ font: "500 13px 'Outfit'", color: "#a1a1aa" }}>We couldn't load your activity.</span>
          <button type="button" onClick={load} style={{ height: 40, padding: "0 18px", borderRadius: 12, background: "#18181C", color: "#fff", font: "700 14px 'Outfit'", border: 0, cursor: "pointer", boxShadow: "inset 0 0 0 1px #2F2F35" }}>Try again</button>
        </div>
      ) : !data ? (
        <div style={{ borderRadius: 18, overflow: "hidden", background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", boxShadow: i ? "inset 0 1px 0 #1C1C20" : undefined }}>
              <span style={{ width: 36, height: 36, borderRadius: 11, background: "#1C1C21" }} />
              <span style={{ flex: 1, height: 12, borderRadius: 4, background: "#1C1C21" }} />
              <span style={{ width: 54, height: 12, borderRadius: 4, background: "#1C1C21" }} />
            </div>
          ))}
        </div>
      ) : txns.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "28px 16px", borderRadius: 18, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          <Icon name="wallet" size={26} style={{ color: "#52525b" }} />
          <span style={{ font: "500 13px 'Outfit'", color: "#a1a1aa" }}>No transactions yet.</span>
        </div>
      ) : (
        <div style={{ borderRadius: 18, overflow: "hidden", background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          {txns.slice(0, 12).map((t, i) => {
            const credit = CREDIT_TYPES.includes(t.type);
            const cur = txCurrency(t);
            const coinMark = cur === "coins";
            const processing = t.type === "withdraw" && !["paid", "sent"].includes(t.status);
            return (
              <div key={t.id || i} data-testid={`m-tx-${t.id || i}`} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 62, padding: "11px 14px", boxShadow: i ? "inset 0 1px 0 #1C1C20" : undefined }}>
                <span style={{ width: 36, height: 36, flex: "none", borderRadius: 11, background: "#1C1C21", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={TYPE_ICON[t.type] || "transfer"} size={16} style={{ color: coinMark ? GREEN : SILVER }} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: "700 14px 'Outfit'", color: "#fff" }}>{TYPE_LABEL[t.type] || t.type}</div>
                  <div style={{ font: "500 12px 'Outfit'", color: "#71717a" }}>
                    {processing ? "Processing" : (t.created_at ? new Date(t.created_at).toLocaleDateString() : "")}
                  </div>
                </div>
                <span className="m-tnum" style={{ display: "inline-flex", alignItems: "center", gap: 5, font: "800 14px 'Manrope'", color: credit ? GREEN : "#fff", whiteSpace: "nowrap" }}>
                  <Icon name={coinMark ? "pnrcoin" : "pnrcash"} size={13} style={{ color: coinMark ? GREEN : SILVER }} />
                  {mask ? "•••" : `${credit ? "+" : "−"}${fmtAmtNum(Math.abs(Number(t.amount) || 0), cur)}`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ font: "500 11px/1.5 'Outfit'", color: "#71717a", margin: "4px 2px 2px" }}>
        PCoins have no cash value. 1 PCash = $1, redeemable from $50 once verified.
      </p>
    </div>
  );
}
